import { useState, useEffect } from 'react';

/**
 * Compute user's Ladder of Wealth stage from existing data
 * Uses Budget, Net Worth, and Diagnosis data without schema changes
 */

// Helper function to fetch user data from backend
const fetchUserFinancialData = async (userId) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    
    // Fetch budget data
    const budgetResponse = await fetch(`${backendUrl}/api/budget/${userId}`);
    const budget = budgetResponse.ok ? await budgetResponse.json() : null;
    
    // Fetch net worth data
    const netWorthResponse = await fetch(`${backendUrl}/api/networth/${userId}`);
    const netWorth = netWorthResponse.ok ? await netWorthResponse.json() : null;
    
    // Fetch diagnosis data
    const diagnosisResponse = await fetch(`${backendUrl}/api/diagnosis/${userId}`);
    const diagnosis = diagnosisResponse.ok ? await diagnosisResponse.json() : null;
    
    return { budget, netWorth, diagnosis };
  } catch (error) {
    console.error('Error fetching financial data:', error);
    return { budget: null, netWorth: null, diagnosis: null };
  }
};

// Helper function to normalize diagnosis data to 5-factor total (5-25 scale)
const normalizeDiagnosisToFiveFactorTotal = (diagnosis) => {
  if (!diagnosis) return 0;
  
  // If we have individual factor scores (assuming 1-5 each)
  const factors = [
    diagnosis.factor1 || 0,
    diagnosis.factor2 || 0, 
    diagnosis.factor3 || 0,
    diagnosis.factor4 || 0,
    diagnosis.factor5 || 0
  ];
  
  const total = factors.reduce((sum, factor) => sum + factor, 0);
  return total > 0 ? total : 0;
};

// Main function to compute ladder stage
const computeLadderStage = (budget, netWorth, diagnosis) => {
  // Extract key financial metrics
  const income = budget?.monthly_income_total || budget?.income || 0;
  const expenses = budget?.monthly_expenses_total || budget?.expenses || 0;
  const surplus = income - expenses;
  const savingsMonthly = budget?.savings_contributions || budget?.savings || 0;
  const savingsRate = income > 0 ? savingsMonthly / income : 0;

  // Net Worth metrics
  const liquidSavings = netWorth?.liquid_savings || netWorth?.emergency_fund || 0;
  const passiveIncomeMonthly = netWorth?.passive_income_monthly || netWorth?.passive_income || 0;
  const efMonths = expenses > 0 ? liquidSavings / expenses : 0;
  const piCover = expenses > 0 ? passiveIncomeMonthly / expenses : 0;

  // Check for positive trend (simplified - could be enhanced with actual trend analysis)
  const trendPositive = surplus > 0 && savingsRate > 0;

  // Diagnosis score (normalize to 5-factor total if present)
  const diagTotal = diagnosis?.five_factor_sum || 
                    normalizeDiagnosisToFiveFactorTotal(diagnosis) || 0;

  // Ladder stage computation rules (matching ProsperMind logic)
  
  // Stage 7: Abundance
  if ((piCover >= 1.25 && efMonths >= 12 && trendPositive) || 
      (diagTotal >= 23 && piCover >= 1.0)) {
    return 7;
  }
  
  // Stage 6: Freedom  
  if ((piCover >= 1.0 && efMonths >= 6) || 
      (diagTotal >= 21 && piCover >= 0.9)) {
    return 6;
  }
  
  // Stage 5: Security
  if ((piCover >= 0.4 || (efMonths >= 6 && savingsRate >= 0.15)) && surplus > 0) {
    return 5;
  }
  
  // Stage 4: Stability
  if ((surplus > 0 && savingsRate >= 0.10) || 
      (diagTotal >= 16 && diagTotal <= 20)) {
    return 4;
  }
  
  // Stage 3: Independence  
  if ((income >= expenses && efMonths < 3) || 
      (diagTotal >= 11 && diagTotal <= 15)) {
    return 3;
  }
  
  // Stage 2: Productivity
  if ((income > 0 && income < expenses) || 
      (diagTotal >= 8 && diagTotal <= 10)) {
    return 2;
  }
  
  // Stage 1: Dependence (default)
  return 1;
};

// React hook to get user's ladder stage
export const useLadderStage = (userId) => {
  const [stage, setStage] = useState(1);
  const [hasData, setHasData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndComputeStage = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const { budget, netWorth, diagnosis } = await fetchUserFinancialData(userId);
        
        // Check if we have enough data to compute stage
        const hasMinimalData = budget || netWorth || diagnosis;
        setHasData(hasMinimalData);
        
        if (hasMinimalData) {
          const computedStage = computeLadderStage(budget, netWorth, diagnosis);
          setStage(computedStage);
        } else {
          setStage(1); // Default to Dependence if no data
        }
      } catch (error) {
        console.error('Error computing ladder stage:', error);
        setStage(1);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAndComputeStage();
  }, [userId]);

  return { stage, hasData, loading };
};

// Alternative selector that uses existing data hooks if available
export const useLadderStageFromContext = (financialData) => {
  const { budget, netWorth, diagnosis } = financialData || {};
  
  const hasMinimalData = budget || netWorth || diagnosis;
  const stage = hasMinimalData ? computeLadderStage(budget, netWorth, diagnosis) : 1;
  
  return { stage, hasData: hasMinimalData };
};

export default useLadderStage;